import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SummaryBlock from "@/components/SummaryBlock";
import SEO from "@/components/SEO";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const CancellationPolicyPage = () => {
  return (
    <>
      <SEO
        title="No-Show & Cancellation Policy | Stillwater Therapy"
        description="Stillwater Therapy's policy on rescheduling, late cancellations, no-shows, fees, and emergencies."
        path="/cancellation-policy"
      />
      <Header />
      <main className="bg-background min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
          <motion.div {...fadeIn} className="mb-16">
            <Link to="/">
              <Button className="mb-8 font-body font-medium tracking-wider text-xs uppercase">
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-3xl md:text-5xl font-heading font-medium text-foreground mb-6">
              No-Show &amp; Cancellation Policy
            </h1>
            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              What to know about rescheduling, cancellations, and missed appointments.
            </p>
          </motion.div>

          {/* Cancellations & No-Shows */}
          <motion.section {...fadeIn} className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-4">
              Cancellations &amp; No-Shows
            </h2>
            <SummaryBlock>
              If you cancel less than 24 hours before your session or miss a session without notice, you'll be charged the full session fee.
            </SummaryBlock>
            <div className="font-body text-sm text-foreground/80 leading-relaxed space-y-3">
              <p>If you need to cancel your session, please cancel or reschedule by phone, email, or the client portal at least 24 hours before your session begins.</p>
              <p>If you cancel with less than 24 hours' notice, you'll be charged the full session fee. If you miss a session without any notice, the same full-session fee applies.</p>
              <p>If a pattern of no-shows occurs (two or more within a sixty-day period), Stillwater Therapy reserves the right to discuss continuation of services and may require prepayment for future sessions.</p>
              <p>Insurance companies do not reimburse for missed or cancelled sessions. This fee is the sole responsibility of the client, regardless of insurance coverage.</p>
              <p>This policy exists to ensure that appointment times can be made available to other clients who may be waiting for services. Your reserved session time is held exclusively for you, and late cancellations prevent others from accessing care.</p>
            </div>
          </motion.section>

          {/* Fees & Payment */}
          <motion.section {...fadeIn} className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-4">
              Fees &amp; Payment
            </h2>
            <div className="font-body text-sm text-foreground/80 leading-relaxed space-y-3">
              <p>Session fees are discussed upfront, and payment is due at the time of service.</p>
              <p>Stillwater Therapy accepts select insurance plans and can provide superbills for out-of-network reimbursement upon request. It is the client's responsibility to verify benefits and coverage with their insurance provider.</p>
            </div>
          </motion.section>

          {/* Exceptions & Emergencies */}
          <motion.section {...fadeIn} className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-4">
              Exceptions &amp; Emergencies
            </h2>
            <div className="font-body text-sm text-foreground/80 leading-relaxed space-y-3">
              <p>Stillwater Therapy recognizes that unforeseen emergencies arise. Genuine emergencies — illness, accidents, family crises — will be handled with understanding on a case-by-case basis. It's up to your therapist's discretion on when and how to enforce the cancellation policy.</p>
              <p>In the event of a genuine emergency (e.g., sudden illness, accident, or family crisis), therapists may be able to offer an alternative session time later in the same week (if any are available). If a session is rescheduled due to an emergency, no cancellation fees will apply.</p>
              <p>Please communicate as soon as reasonably possible if an emergency prevents you from attending your session.</p>
            </div>
          </motion.section>

          {/* Repeated Missed Sessions */}
          <motion.section {...fadeIn} className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-4">
              Repeated Missed Sessions
            </h2>
            <SummaryBlock>
              Consistent attendance matters for progress. If missed sessions become a pattern, we'll talk about it together and figure out a plan.
            </SummaryBlock>
            <div className="font-body text-sm text-foreground/80 leading-relaxed space-y-3">
              <p>Consistent attendance is an important component of effective therapy. If a client accumulates three or more late cancellations or no-shows within a ninety-day period, Stillwater Therapy may:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Initiate a discussion about barriers to attendance and explore potential solutions.</li>
                <li>Adjust session frequency or scheduling to better accommodate the client's needs.</li>
                <li>Require prepayment for future scheduled sessions.</li>
                <li>In exceptional circumstances, provide a referral to another provider who may better serve the client's needs.</li>
              </ul>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.div {...fadeIn} className="text-center pt-8 border-t border-border">
            <h3 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-3">
              Still Have Questions?
            </h3>
            <p className="font-body text-muted-foreground font-light mb-6">
              If anything about this policy is unclear or you'd like to talk it through, please reach out.
            </p>
            <a href="mailto:ashley@stillwatertherapy.com">
              <Button className="font-body font-medium tracking-wider text-xs uppercase">
                Contact Us
              </Button>
            </a>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CancellationPolicyPage;
